import React, { useState } from 'react';
import { Share2, Twitter, Facebook, Linkedin, MessageCircle, Link2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');

const ShareButtons = ({ newsId, slug, title, summary }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  // Prefer the clean slug URL for sharing (better SEO + previews)
  const shareUrl = `${SITE_URL}/news/${slug || newsId}`;
  // Use the witty summary as the share text if available
  const shareText = summary ? `"${summary}" - Daily Wit` : `Check out: ${title}`;

  const logShare = async (platform) => {
    try {
      await axios.post(`${API}/share/${newsId}?platform=${platform}`);
    } catch (error) {
      console.error('Error logging share:', error);
    }
  };

  const handleShare = (platform) => {
    let shareLink = '';
    
    switch (platform) {
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareLink, '_blank', 'width=600,height=400');
    logShare(platform);
    setShowMenu(false);
    toast.success(`Shared on ${platform}!`);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      logShare('link');
      toast.success('Link copied to clipboard!');
      setTimeout(() => {
        setCopied(false);
        setShowMenu(false);
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: shareUrl
        });
        logShare('native');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      setShowMenu(!showMenu);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleNativeShare}
        data-testid={`share-button-${newsId}`}
        className="p-2 rounded-full border-2 border-black bg-white hover:bg-[#4ECDC4] transition-all hard-shadow-hover"
      >
        <Share2 className="w-5 h-5" strokeWidth={2.5} />
      </button>

      {showMenu && (
        <div className="absolute right-0 top-12 bg-white border-2 border-black rounded-xl hard-shadow-lg p-2 z-50 min-w-[200px]">
          <button
            onClick={() => handleShare('twitter')}
            data-testid="share-twitter"
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#F0F0F0] rounded-lg transition-all text-left"
          >
            <Twitter className="w-5 h-5" strokeWidth={2.5} />
            <span className="font-bold">Twitter</span>
          </button>
          <button
            onClick={() => handleShare('facebook')}
            data-testid="share-facebook"
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#F0F0F0] rounded-lg transition-all text-left"
          >
            <Facebook className="w-5 h-5" strokeWidth={2.5} />
            <span className="font-bold">Facebook</span>
          </button>
          <button
            onClick={() => handleShare('linkedin')}
            data-testid="share-linkedin"
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#F0F0F0] rounded-lg transition-all text-left"
          >
            <Linkedin className="w-5 h-5" strokeWidth={2.5} />
            <span className="font-bold">LinkedIn</span>
          </button>
          <button
            onClick={() => handleShare('whatsapp')}
            data-testid="share-whatsapp"
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#F0F0F0] rounded-lg transition-all text-left"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
            <span className="font-bold">WhatsApp</span>
          </button>
          <div className="border-t-2 border-black my-2"></div>
          <button
            onClick={handleCopyLink}
            data-testid="share-copy-link"
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#FFE66D] rounded-lg transition-all text-left"
          >
            {copied ? (
              <Check className="w-5 h-5 text-green-500" strokeWidth={2.5} />
            ) : (
              <Link2 className="w-5 h-5" strokeWidth={2.5} />
            )}
            <span className="font-bold">{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButtons;